import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ROUTE_PARAMS } from '../../../utilities/navigation';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Select, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { Cancel, Delete, Edit, Save } from '@mui/icons-material';
import { EAudience } from '../../../types/db';
import { useHelpCenterNavigation } from '../../../hooks/help/useHelpCenterNavigation';
import { useHelpDocs, useHelpInfo } from '../../../hooks/help/useHelpDocs';

/** Manage the topics in the help center */
export const HelpManageTopicsPage = () => {
    const { [ROUTE_PARAMS.topicID]: topicId } = useParams();
    if (!topicId) {
        //general topics page
        return <HelpManageTopicsContainer />;
    }
    return <HelpManageTopicContainer topicId={topicId} />;
};

const HelpManageTopicsContainer = () => {
    const { navigateToManage } = useHelpCenterNavigation();
    const { docs: topics, isLoading, create, delete: deleteTopic } = useHelpDocs('topic');
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const handleAddTopic = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const title = formJson.title;
        const description = formJson.description || undefined;
        const audience = formJson.audience || EAudience.COMMON;
        const parent_id = formJson.parent || null;
        create({
            title,
            description,
            audience,
            parent_id,
        }).then(() => {
            setAddDialogOpen(false);
        })
    }

    return <Stack direction='column'>
        <Button onClick={() => setAddDialogOpen(true)}>Add topic</Button>
        {
            !isLoading ?
                topics.map(topic =>
                    <Stack key={topic.id} direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography>{topic.title}</Typography>
                        <Typography flex={1}>{topic.description || ''}</Typography>
                        <IconButton onClick={() => navigateToManage("topic", topic.id)}>
                            <Edit />
                        </IconButton>
                        <IconButton onClick={() => deleteTopic(topic.id)}>
                            <Delete />
                        </IconButton>
                    </Stack>
                )
                :
                <Skeleton variant='rectangular' width={'100%'} height={'100px'} />
        }
        <Dialog open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}>
            <DialogTitle>Add topic</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Add Topic
                </DialogContentText>
                <form onSubmit={handleAddTopic} id="form">
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        name="title"
                        label="Title"
                        type="text"
                        fullWidth
                        variant="standard"
                    />
                    <TextField
                        margin="dense"
                        name="description"
                        label="Descrition"
                        type="text"
                        fullWidth
                        variant="standard"
                        multiline
                    />
                    <Select
                        name='audience'
                        label='Audience'
                        variant='standard'
                        fullWidth
                        defaultValue={EAudience.COMMON}
                    >
                        <MenuItem value={EAudience.COMMON}>Common</MenuItem>
                        <MenuItem value={EAudience.HOST}>Host</MenuItem>
                        <MenuItem value={EAudience.COHOST}>CoHost</MenuItem>
                        <MenuItem value={EAudience.GUEST}>Guest</MenuItem>
                    </Select>
                    <Select
                        name='parent'
                        label='Parent Topic'
                        variant='standard'
                        fullWidth
                        defaultValue={''}
                    >
                        <MenuItem key={'none'} value={''}>None</MenuItem>
                        {
                            topics.map(topic =>
                                <MenuItem key={topic.id} value={topic.id}>{topic.title}</MenuItem>
                            )}
                    </Select>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" form="form">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    </Stack>
}

/** View a topic in edit mode (being able to edit and delete) */
const HelpManageTopicContainer = ({ topicId }: { topicId: string }) => {
    const { navigateToManage } = useHelpCenterNavigation();
    const { info: topic, isLoading, isError, update, delete: deleteIt } = useHelpInfo(topicId);
    const [localTopic, setLocalTopic] = useState(topic);
    useEffect(() => {
        if (!topic) return;
        setLocalTopic(topic)
    }, [topic]);

    if (isError || !topic || !localTopic) {
        return null;
    }

    return <Stack direction='column'>
        {
            !isLoading ?
                <Stack direction={'column'} spacing={2}>
                    <TextField
                        label="title"
                        value={localTopic.title || ''}
                        onChange={(e) => setLocalTopic(lt => ({ ...lt!, title: e.target.value }))}
                        required
                    />
                    <TextField
                        label="description"
                        value={localTopic.description || ''}
                        onChange={(e) => setLocalTopic(lt => ({ ...lt!, description: e.target.value }))}
                    />
                    <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'}>
                        <IconButton onClick={() => navigateToManage("topic")}>
                            <Cancel />
                        </IconButton>
                        <IconButton onClick={() => update(localTopic).then(() => { navigateToManage("topic") })}>
                            <Save />
                        </IconButton>
                        <IconButton onClick={() => deleteIt().then(() => { navigateToManage("topic") })}>
                            <Delete />
                        </IconButton>
                    </Stack>
                </Stack>
                :
                <Skeleton variant='rectangular' width={'100%'} height={'100px'} />
        }
    </Stack>
}